<script setup>
import { computed } from 'vue';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: '',
  },
  emoji: {
    type: String,
    default: '',
  },
  isOpen: {
    type: Boolean,
    default: true,
  },
  count: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['toggle']);

const isLucideIcon = computed(
  () => typeof props.icon === 'string' && props.icon.startsWith('i-')
);

const lucideIconClass = computed(() => {
  const size = props.compact ? 'size-3.5' : 'size-4';
  return `${props.icon} ${size} shrink-0 text-n-slate-11`;
});

const onToggle = () => {
  emit('toggle');
};
</script>

<template>
  <div class="text-sm">
    <button
      class="flex items-center select-none w-full rounded-lg bg-n-slate-2 outline outline-1 outline-n-weak m-0 cursor-grab justify-between drag-handle"
      :class="[
        compact ? 'py-1.5 px-3' : 'py-2 px-4',
        { 'rounded-bl-none rounded-br-none': isOpen },
      ]"
      @click.stop="onToggle"
    >
      <div class="flex items-center gap-1.5 min-w-0">
        <span v-if="isLucideIcon" :class="lucideIconClass" />
        <EmojiOrIcon
          v-else-if="icon || emoji"
          class="inline-block shrink-0"
          :class="compact ? 'w-4' : 'w-5'"
          :icon="icon"
          :emoji="emoji"
        />
        <h5 class="text-n-slate-12 text-sm mb-0 py-0 pr-2 pl-0 truncate">
          {{ title }}
        </h5>
        <span
          v-if="count !== null"
          data-testid="accordion-count"
          class="shrink-0 rounded-full bg-n-alpha-2 px-1.5 text-xs tabular-nums text-n-slate-11"
        >
          {{ count }}
        </span>
      </div>
      <div class="flex flex-row items-center gap-1 ml-2 shrink-0">
        <slot name="button" />
        <span
          class="size-4 text-n-slate-11"
          :class="isOpen ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
          data-testid="accordion-chevron"
        />
      </div>
    </button>
    <div
      v-if="isOpen"
      class="outline outline-1 -mt-[-1px] border-t-0 rounded-br-lg rounded-bl-lg"
      :class="compact ? 'p-1.5 outline-n-strong' : 'px-2 py-4 outline-n-weak'"
    >
      <slot />
    </div>
  </div>
</template>
